import type { ISelectOption, TTranslate } from '@/interfaces';
import { uniqSorted } from 'common/utils/array.utils';

export const getSchoolLevelDisplayLabel = (schoolLevel: number, t: TTranslate): string => {
  if (schoolLevel === 0) {
    return t('general.schoolLevelPreSchool');
  }

  return String(schoolLevel);
};

export const buildSchoolLevelOptions = (params: {
  schoolLevels: number[];
  t: TTranslate;
  useRawSchoolLevelLabels?: boolean;
}): ISelectOption<number>[] => {
  if (params.schoolLevels.length === 0) return [];

  // Normalize once (unique + sorted) so the OUTPUT options are deduplicated and ordered regardless of caller input.
  // "Adjusted" (relative) labels renumber the levels consecutively from 1 by their sorted rank — correct wherever the
  // range starts and in any input order (e.g. 7–12 → 1–6, 4–10 → 1–7). Pre-school (0) keeps its own label and is not
  // counted in the rank. "Raw" labels show the real (global) level number.
  const sortedLevels = uniqSorted(params.schoolLevels);
  const rankByLevel = new Map<number, number>(
    sortedLevels.filter((level) => level !== 0).map((level, index) => [level, index + 1]),
  );

  return sortedLevels.map((schoolLevel) => {
    if (schoolLevel === 0) {
      return {
        value: schoolLevel,
        label: params.t('general.schoolLevelPreSchool'),
      };
    }

    const displayLevel =
      params.useRawSchoolLevelLabels === true ? schoolLevel : (rankByLevel.get(schoolLevel) ?? schoolLevel);

    return {
      value: schoolLevel,
      label: params.t('schoolSubjects.filter', {
        schoolLevel: displayLevel,
      }),
    };
  });
};
